import { createAction, props } from '@ngrx/store';
import { UntypedFormGroup } from '@angular/forms';
import { EndPage } from '../models/endPage';
import { CompletedStaticForm } from 'Shared/app/models/completedStaticForm';
import { FormType } from 'Shared/app/enums/FormType';

export const reset = createAction('[Static Form] Reset');
export const initialize = createAction('[Static Form] Initialize', props<{ pages: unknown[] }>());

export const exitForm = createAction('[Static Form] Exit Form', props<{ rerouteUrl: string }>());

export const saveForm = createAction(
  '[Static Form] Start Save Form',
  props<{
    bookingId: string;
    bookedFormId: string;
    formId: string;
    formGroup: UntypedFormGroup;
    hasEndPage?: EndPage;
    isRedo?: boolean;
  }>()
);
export const saveFormCompleted = createAction(
  '[Static Form] Save Form Completed',
  props<{
    bookingId: string;
    bookedFormId: string;
    formId: string;
    formGroup: UntypedFormGroup;
    hasEndPage?: EndPage;
  }>()
);
export const saveFormFailed = createAction('[Static Form] Save Form Failed', props<{ error: Error }>());
export const setShouldStopBeforeSaving = createAction(
  '[Static Form] Set Should Stop Before Saving',
  props<{ shouldStopBeforeSaving: boolean }>()
);
export const setStoppedBeforeSaving = createAction(
  '[Static Form] Set Stopped Before Saving',
  props<{ stoppedBeforeSaving: boolean }>()
);
export const setShouldForceSave = createAction(
  '[Static Form] Set Should Force Save',
  props<{ shouldForceSave: boolean }>()
);

export const saveDraft = createAction(
  '[Static Form] Start Save Draft',
  props<{
    bookingId: string;
    bookedFormId: string;
    formId: string;
    formGroup: UntypedFormGroup;
    continueDraft: boolean;
    isRedo?: boolean;
  }>()
);
export const saveDraftCompleted = createAction(
  '[Static Form] Save Draft Completed',
  props<{ continueDraft: boolean }>()
);
export const saveDraftFailed = createAction('[Static Form] Save Draft Failed', props<{ error: Error }>());
export const setShouldStopBeforeDrafting = createAction(
  '[Static Form] Set Should Stop Before Drafting',
  props<{ shouldStopBeforeDrafting: boolean }>()
);
export const setStoppedBeforeDrafting = createAction(
  '[Static Form] Set Stopped Before Drafting',
  props<{ stoppedBeforeDrafting: boolean }>()
);
export const setShouldForceDraft = createAction(
  '[Static Form] Set Should Force Draft',
  props<{ shouldForceDraft: boolean }>()
);

export const cancelForm = createAction(
  '[Static Form] Start cancelling form',
  props<{ bookedForm; modalResultData; rerouteUrl }>()
);
export const cancelFormCompleted = createAction('[Static Form] End cancelling form');
export const cancelFormFailed = createAction('[Static Form] Cancel form failed', props<{ error: Error }>());

export const updateForm = createAction(
  '[Static Form] Start Update Form',
  props<{
    bookingId: string;
    bookedFormId: string;
    formId: string;
    languageId: number;
    formGroup: UntypedFormGroup;
    performedAt: Date;
  }>()
);
export const updateFormCompleted = createAction(
  '[Static Form] Update Form Completed',
  props<{ bookedFormId: string }>()
);
export const updateFormFailed = createAction('[Static Form] Update Form Failed', props<{ error: Error }>());
export const setShouldStopBeforeUpdating = createAction(
  '[Static Form] Set Should Stop Before Updating',
  props<{ shouldStopBeforeUpdating: boolean }>()
);
export const setStoppedBeforeUpdating = createAction(
  '[Static Form] Set Stopped Before Updating',
  props<{ stoppedBeforeUpdating: boolean }>()
);
export const setShouldForceUpdate = createAction(
  '[Static Form] Set Should Force Update',
  props<{ shouldForceUpdate: boolean }>()
);

export const getCompletedForm = createAction('[Static Form] Get Completed Form', props<{ bookedFormId: string }>());
export const getCompletedFormCompleted = createAction(
  '[Static Form] Get Completed Form Completed',
  props<{ completedForm: CompletedStaticForm }>()
);
export const getCompletedFormFailed = createAction(
  '[Static Form] Get Completed Form Failed',
  props<{ error: Error }>()
);
export const clearCompletedForm = createAction('[Static Form] Clear Completed Form');

export const closeForm = createAction('[Static Form] Close Form');

export const setIsLoading = createAction('[Static Form] Set Loading', props<{ isLoading: boolean }>());
export const setIsEditMode = createAction('[Static Form] Set Edit Mode', props<{ isEditMode: boolean }>());
export const setTypeName = createAction('[Static Form] Set TypeName', props<{ typeName: string }>());
export const setFormType = createAction('[Static Form] Set Form type', props<{ formType: FormType }>());
export const setCurrentPage = createAction('[Static Form] Set Current Page', props<{ currentPage: number }>());
export const setFormHasChanges = createAction('[Static Form] Set Has Changes', props<{ hasChanges: boolean }>());
export const setIsPostRegister = createAction('[Static Form] Set Post Register', props<{ isPostRegister: boolean }>());
export const setPaginationVisibility = createAction(
  '[Static Form] Set Pagination Visibility',
  props<{ paginationVisible: boolean }>()
);
export const setVisiblePages = createAction('[Static Form] Set Visible Pages', props<{ visiblePages: number[] }>());
export const setPageCountTotal = createAction('[Static Form] Set Page Count Total', props<{ pageCount: number }>());
export const setShowAllPages = createAction('[Static Form] Show All Pages');
export const setIsPerformView = createAction('[Static Form] Set IsPerformView', props<{ isPerformView: boolean }>());
export const setIsCareProvider = createAction('[Static Form] Set IsCareProvider', props<{ isCareProvider: boolean }>());
export const setIsPrinting = createAction('[Static Form] Set IsPrinting', props<{ isPrinting: boolean }>());
export const setShouldStopBeforeNextPage = createAction(
  '[Static Form] Set ShouldStopOnNextPage',
  props<{ shouldStopBeforeNextPage: boolean }>()
);
export const setSaveButtonClicked = createAction(
  '[Static Form] Set SaveButtonClicked',
  props<{ saveButtonClicked: boolean }>()
);
export const setNextPageButtonClicked = createAction(
  '[Static Form] Set NextPageButtonClicked',
  props<{ nextPageButtonClicked: boolean }>()
);

export const setErrorCount = createAction('[Static Form] Set Error Count', props<{ errors: number }>());
export const incrementErrorCount = createAction(
  '[Static Form] Increment Error Count',
  props<{ incrementBy: number }>()
);
export const requestPageValidation = createAction(
  '[Static Form] Request Page Validation',
  (questionsForValidationIds: string[] | null = null) => ({ questionsForValidationIds })
);
export const completePageValidation = createAction('[Static Form] Page Validation Completed');
export const setLastValidatedPage = createAction(
  '[Static Form] Set Last Validated Page',
  props<{ pageNumber: number }>()
);

export const logWritten = createAction('[Static Form] Audit Log Written');

export const setOnNextPage = createAction(
  '[Static Form] Set On Next Page function',
  props<{ onNextPage: (currentPage: number) => void }>()
);
export const setOnPreviousPage = createAction(
  '[Static Form] Set On Previous Page function',
  props<{ onPreviousPage: (currentPage: number) => void }>()
);

export const setAdditionalQuestionsToConvert = createAction(
  '[Static Form] Set Additional Questions To Convert',
  props<{ additionalQuestionsToConvert: unknown[] }>()
);

export const setShouldFocusFormName = createAction(
  '[Static Form] Should Focus Form Name',
  props<{ shouldFocusFormName: boolean; shouldReadInstructions: boolean }>()
);

export const setJournalizationDisabled = createAction(
  '[Static Form] Set Journalization Disabled',
  props<{ journalizationDisabled: boolean }>()
);

export const setNextPageLabel = createAction('[Static Form] Set Next Page Label', props<{ nextPageLabel: string }>());

export const setCustomErrorMessages = createAction(
  '[Static Form] Set Custom Error Messages In Form Paginator',
  props<{ customErrorMessages: string[] }>()
);
